import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import {
  fetchTeamMembers, fetchTeamMemberStats,
  fetchTeamInvites, fetchTeamActivity, fetchTeamSkills,
  sendTeamInvite, revokeInvite, removeMember, updateMemberRole,
  logActivity,
  fetchTeamFeed, fetchPendingShares, approveShare, rejectShare,
  fetchTeamFolders, createTeamFolder, deleteTeamFolder, updateTeamFolder, moveShareToFolder,
} from '../lib/supabase';
import { TYPE_ICONS, formatDate, OUTPUT_TYPES } from '../constants';
import DetailPanel from '../components/DetailPanel';

// ── Small helpers ────────────────────────────────────────────
function Avatar({ name, size = 32 }) {
  const initials = (name || '?').slice(0, 2).toUpperCase();
  const colors = ['#7f77dd','#1d9e75','#ef9f27','#e24b4a','#85b7eb'];
  const color = colors[name?.charCodeAt(0) % colors.length] || colors[0];
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: `${color}22`, border: `1.5px solid ${color}55`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.36, fontWeight: 600, color,
    }}>{initials}</div>
  );
}

function RoleBadge({ role }) {
  const map = {
    owner: { bg: 'rgba(239,159,39,0.15)', color: '#ef9f27', border: 'rgba(239,159,39,0.3)', label: 'Owner' },
    admin: { bg: 'var(--accent-dim)',      color: 'var(--accent)', border: 'var(--accent-border)', label: 'Admin' },
    member:{ bg: 'rgba(95,94,90,0.12)',    color: 'var(--muted)', border: 'transparent', label: 'Member' },
  };
  const c = map[role] || map.member;
  return (
    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: c.bg, color: c.color, border: `0.5px solid ${c.border}` }}>
      {c.label}
    </span>
  );
}

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '14px 18px', flex: 1, minWidth: 100 }}>
      <div style={{ fontSize: 26, fontWeight: 500, color: color || 'var(--text)', letterSpacing: '-0.02em', marginBottom: 2 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: sub ? 2 : 0 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--dim)' }}>{sub}</div>}
    </div>
  );
}

// ── Invite modal ─────────────────────────────────────────────
function InviteModal({ team, onClose, onInvited }) {
  const { user, profile } = useAuth();
  const [emails, setEmails]   = useState('');
  const [role, setRole]       = useState('member');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState([]);

  const handle = async () => {
    const list = emails.split(/[\s,;]+/).map(e => e.trim().toLowerCase()).filter(Boolean);
    if (!list.length) return;
    setSending(true);
    const res = [];
    for (const email of list) {
      try {
        const invite = await sendTeamInvite(team.id, email, role);
        // Send invite email
        await fetch('/api/team-invite', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email, token: invite.token,
            teamName: team.name,
            inviterName: profile?.display_name || profile?.username || 'Someone',
            role,
          }),
        }).catch(() => {});
        res.push({ email, ok: true });
        logActivity(team.id, 'invite_sent', null, 'member', { email, role });
      } catch (e) {
        res.push({ email, ok: false, error: e.message });
      }
    }
    setResults(res);
    setSending(false);
    onInvited && onInvited();
  };

  const allDone = results.length > 0;

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.modalHeader}>
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>Invite team members</span>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!allDone ? (
            <>
              <div style={s.field}>
                <label style={s.label}>Email addresses</label>
                <textarea
                  style={{ ...s.input, minHeight: 80, resize: 'vertical', fontFamily: 'var(--font)', lineHeight: 1.6 }}
                  placeholder="alice@company.com, bob@company.com"
                  value={emails}
                  onChange={e => setEmails(e.target.value)} />
                <div style={{ fontSize: 11, color: 'var(--dim)' }}>Separate multiple emails with commas or new lines</div>
              </div>
              <div style={s.field}>
                <label style={s.label}>Role</label>
                <div style={{ display: 'flex', gap: 7 }}>
                  {[
                    { id: 'member', label: '👤 Member', desc: 'Can add and view prompts' },
                    { id: 'admin',  label: '🛡 Admin',  desc: 'Can manage members and settings' },
                  ].map(r => (
                    <div key={r.id} onClick={() => setRole(r.id)} style={{
                      flex: 1, padding: '10px 12px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                      border: `0.5px solid ${role === r.id ? 'var(--accent-border)' : 'var(--border2)'}`,
                      background: role === r.id ? 'var(--accent-dim)' : 'var(--surface2)',
                    }}>
                      <div style={{ fontSize: 13, color: role === r.id ? 'var(--accent)' : 'var(--text)', fontWeight: 500, marginBottom: 2 }}>{r.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--dim)' }}>{r.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={onClose} style={s.cancelBtn}>Cancel</button>
                <button onClick={handle} disabled={sending || !emails.trim()} style={{ ...s.saveBtn, opacity: (!emails.trim() || sending) ? 0.5 : 1 }}>
                  {sending ? 'Sending...' : 'Send invites'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {results.map(r => (
                  <div key={r.email} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                    background: r.ok ? 'rgba(29,158,117,0.08)' : 'rgba(226,75,74,0.08)',
                    border: `0.5px solid ${r.ok ? 'rgba(29,158,117,0.3)' : 'rgba(226,75,74,0.3)'}`,
                    borderRadius: 'var(--radius-sm)',
                  }}>
                    <span style={{ fontSize: 14 }}>{r.ok ? '✓' : '✗'}</span>
                    <div>
                      <div style={{ fontSize: 13, color: 'var(--text)' }}>{r.email}</div>
                      {r.ok
                        ? <div style={{ fontSize: 11, color: '#1d9e75' }}>Invite sent — they'll receive an email shortly</div>
                        : <div style={{ fontSize: 11, color: 'var(--danger)' }}>{r.error}</div>
                      }
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => { onInvited && onInvited(); onClose(); }} style={s.saveBtn}>Done</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Team Folders Tab ─────────────────────────────────────────
const FOLDER_COLORS = ['#7f77dd','#1d9e75','#e24b4a','#ef9f27','#85b7eb','#5dcaa5','#f09595','#afa9ec'];

function TeamFoldersTab({ teamId, folders, isTeamAdmin, settings, onCreateFolder, onDeleteFolder, onUpdateFolder }) {
  const [newName, setNewName]   = useState('');
  const [newColor, setNewColor] = useState(FOLDER_COLORS[0]);
  const [creating, setCreating] = useState(false);
  const [editId, setEditId]     = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [saving, setSaving]     = useState(false);

  const canCreate = isTeamAdmin || settings.members_can_create_folders !== false;

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try { await onCreateFolder(newName.trim(), newColor); setNewName(''); setNewColor(FOLDER_COLORS[0]); }
    catch (e) { alert(e.message); }
    setCreating(false);
  };

  const startEdit = (f) => { setEditId(f.id); setEditName(f.name); setEditColor(f.color || FOLDER_COLORS[0]); };
  const cancelEdit = () => { setEditId(null); setEditName(''); setEditColor(''); };

  const handleUpdate = async (folderId) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await onUpdateFolder(folderId, { name: editName.trim(), color: editColor });
      cancelEdit();
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Clarifying note */}
      <div style={{
        fontSize: 12, color: 'var(--dim)', lineHeight: 1.6,
        background: 'var(--surface2)', borderRadius: 8, padding: '8px 12px',
        border: '0.5px solid var(--border)',
      }}>
        📁 Team folders are <strong style={{ color: 'var(--muted)' }}>shared</strong> within this team only.
        They appear separately from your personal folders in the sidebar.
      </div>

      {/* Folder list */}
      {folders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--dim)', fontSize: 13 }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>📁</div>
          No team folders yet
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {folders.map(f => (
            <div key={f.id} style={{
              background: 'var(--surface)', border: `0.5px solid ${editId === f.id ? 'var(--accent-border)' : 'var(--border)'}`,
              borderRadius: 9, overflow: 'hidden', transition: 'border-color 0.15s',
            }}>
              {editId === f.id ? (
                /* Edit mode */
                <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <input
                    autoFocus
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleUpdate(f.id)}
                    style={{ background: 'var(--bg)', border: '0.5px solid var(--border2)', borderRadius: 7, padding: '7px 10px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, outline: 'none' }}
                  />
                  {/* Color swatches */}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {FOLDER_COLORS.map(c => (
                      <button key={c} onClick={() => setEditColor(c)} style={{
                        width: 24, height: 24, borderRadius: 6, background: c, border: 'none',
                        outline: editColor === c ? '2.5px solid var(--text)' : 'none',
                        outlineOffset: 2, cursor: 'pointer', flexShrink: 0,
                        transform: editColor === c ? 'scale(1.15)' : 'scale(1)', transition: 'transform 0.1s',
                      }} />
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 7 }}>
                    <button onClick={() => handleUpdate(f.id)} disabled={saving || !editName.trim()} style={{
                      flex: 1, background: 'var(--accent)', color: '#fff', border: 'none',
                      borderRadius: 7, padding: '7px', fontFamily: 'var(--font)', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    }}>{saving ? 'Saving...' : 'Save'}</button>
                    <button onClick={cancelEdit} style={{
                      background: 'none', border: '0.5px solid var(--border2)', borderRadius: 7, padding: '7px 12px',
                      fontFamily: 'var(--font)', fontSize: 12, color: 'var(--muted)', cursor: 'pointer',
                    }}>Cancel</button>
                  </div>
                </div>
              ) : (
                /* View mode */
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px' }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                    background: f.color || '#7f77dd',
                    boxShadow: `0 2px 6px ${f.color || '#7f77dd'}40`,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{f.name}</div>
                  </div>
                  {isTeamAdmin && (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => startEdit(f)} style={{
                        background: 'none', border: 'none', color: 'var(--dim)',
                        cursor: 'pointer', fontSize: 13, padding: '3px 7px', borderRadius: 5,
                      }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--text)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--dim)'}>
                        ✎
                      </button>
                      <button onClick={() => onDeleteFolder(f.id)} style={{
                        background: 'none', border: 'none', color: 'var(--dim)',
                        cursor: 'pointer', fontSize: 16, padding: '2px 4px',
                      }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--dim)'}>
                        ×
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create folder */}
      {canCreate && (
        <div style={{
          border: '0.5px dashed var(--border2)', borderRadius: 10,
          padding: '14px 16px', background: 'var(--surface)',
        }}>
          <div style={{ fontSize: 11, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
            New team folder
          </div>
          <input
            style={{
              background: 'var(--bg)', border: '0.5px solid var(--border2)',
              borderRadius: 7, padding: '8px 12px', color: 'var(--text)',
              fontFamily: 'var(--font)', fontSize: 13, outline: 'none',
              width: '100%', marginBottom: 10,
            }}
            placeholder="Folder name..."
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
          />
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
            {FOLDER_COLORS.map(c => (
              <button key={c} onClick={() => setNewColor(c)} style={{
                width: 26, height: 26, borderRadius: 6, background: c, border: 'none',
                outline: newColor === c ? `2.5px solid var(--text)` : 'none',
                outlineOffset: 2, cursor: 'pointer', flexShrink: 0,
                transform: newColor === c ? 'scale(1.15)' : 'scale(1)',
                transition: 'transform 0.1s',
              }} />
            ))}
          </div>
          <button
            onClick={handleCreate}
            disabled={!newName.trim() || creating}
            style={{
              background: '#7f77dd', color: '#fff', border: 'none',
              borderRadius: 7, padding: '8px 16px',
              fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500,
              cursor: !newName.trim() || creating ? 'not-allowed' : 'pointer',
              opacity: !newName.trim() ? 0.5 : 1,
            }}>
            {creating ? 'Creating...' : '+ Create folder'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main dashboard ────────────────────────────────────────────
export default function TeamDashboard() {
  const { user } = useAuth();
  const { activeTeam, isTeamAdmin, isTeamOwner, teamRole, loadingTeams } = useTeam();
  const navigate = useNavigate();

  const [tab, setTab]             = useState('feed');
  const [entries, setEntries]     = useState([]);
  const [members, setMembers]     = useState([]);
  const [memberStats, setMemberStats] = useState([]);
  const [invites, setInvites]     = useState([]);
  const [activity, setActivity]   = useState([]);
  const [skills, setSkills]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showInvite, setShowInvite]   = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [pendingShares, setPendingShares] = useState([]);
  const [teamFolders, setTeamFolders] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [activeFolderFilter, setActiveFolderFilter] = useState(null); // null = all
  const [draggingShareId, setDraggingShareId] = useState(null);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);

  const teamId = activeTeam?.id;
  const isTeamAdminRef = useRef(isTeamAdmin);
  isTeamAdminRef.current = isTeamAdmin;

  useEffect(() => {
    if (!teamId) { navigate('/app'); return; }
    let cancelled = false;

    const doLoad = async () => {
      setLoading(true);
      try {
        if (tab === 'feed') {
          const [e, pend, folders] = await Promise.all([
            fetchTeamFeed(teamId, { status: 'approved' }),
            isTeamAdminRef.current ? fetchPendingShares(teamId) : Promise.resolve([]),
            fetchTeamFolders(teamId),
          ]);
          console.log('[TeamDashboard] fetch done, cancelled:', cancelled, 'entries:', e?.length);
          if (cancelled) return;
          setEntries(e);
          setPendingShares(pend);
          setPendingCount(pend.length);
          setTeamFolders(folders);
        } else if (tab === 'pending') {
          const pend = await fetchPendingShares(teamId);
          if (cancelled) return;
          setPendingShares(pend);
          setPendingCount(pend.length);
        } else if (tab === 'folders') {
          const folders = await fetchTeamFolders(teamId);
          if (cancelled) return;
          setTeamFolders(folders);
        } else if (tab === 'members') {
          const [m, st, inv] = await Promise.all([
            fetchTeamMembers(teamId),
            isTeamAdminRef.current ? fetchTeamMemberStats(teamId) : Promise.resolve([]),
            isTeamAdminRef.current ? fetchTeamInvites(teamId) : Promise.resolve([]),
          ]);
          if (cancelled) return;
          setMembers(m); setMemberStats(st); setInvites(inv);
        } else if (tab === 'activity') {
          const a = await fetchTeamActivity(teamId);
          if (cancelled) return;
          setActivity(a);
        } else if (tab === 'skills') {
          const sk = await fetchTeamSkills(teamId);
          if (cancelled) return;
          setSkills(sk);
        }
      } catch (e) { console.error('TeamDashboard load error:', e?.message); }
      if (!cancelled) setLoading(false);
    };

    doLoad();
    return () => { cancelled = true; };
  }, [teamId, tab]);

  const load = (tId, t, a) => {}; // kept for invite callback compatibility

  const filteredEntries = useMemo(() => {
    let list = entries;
    if (activeFolderFilter) list = list.filter(e => e.teamFolderId === activeFolderFilter);
    if (typeFilter !== 'all') list = list.filter(e => e.type === typeFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(e => e.title?.toLowerCase().includes(q) || e.sharedBy?.username?.toLowerCase().includes(q));
    }
    return list;
  }, [entries, activeFolderFilter, typeFilter, search]);

  const handleApprove = async (shareId) => {
    await approveShare(shareId);
    setPendingShares(prev => prev.filter(s => s.shareId !== shareId));
    setPendingCount(c => Math.max(0, c - 1));
    // Reload feed to show newly approved entry
    if (tab === 'feed') {
      const e = await fetchTeamFeed(teamId, { status: 'approved' });
      setEntries(e);
    }
  };

  const handleReject = async (shareId, note = '') => {
    await rejectShare(shareId, note);
    setPendingShares(prev => prev.filter(s => s.shareId !== shareId));
    setPendingCount(c => Math.max(0, c - 1));
  };

  const handleCreateFolder = async (name, color) => {
    const folder = await createTeamFolder(teamId, name, color);
    setTeamFolders(prev => [...prev, folder]);
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Entries will stay in the team feed.')) return;
    await deleteTeamFolder(folderId);
    setTeamFolders(prev => prev.filter(f => f.id !== folderId));
  };

  const handleUpdateFolder = async (folderId, updates) => {
    const updated = await updateTeamFolder(folderId, updates);
    setTeamFolders(prev => prev.map(f => f.id === folderId ? { ...f, ...updated } : f));
  };

  const handleDropToFolder = async (shareId, folderId) => {
    setDraggingShareId(null);
    setDragOverFolderId(null);
    try {
      await moveShareToFolder(shareId, folderId);
      setEntries(prev => prev.map(e => e.shareId === shareId ? { ...e, teamFolderId: folderId } : e));
    } catch (e) { alert(e.message); }
  };

  const handleRoleChange = async (userId, newRole) => {
    await updateMemberRole(teamId, userId, newRole);
    setMembers(prev => prev.map(m => m.user_id === userId ? { ...m, role: newRole } : m));
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this member from the team?')) return;
    await removeMember(teamId, userId);
    setMembers(prev => prev.filter(m => m.user_id !== userId));
  };

  const handleRevokeInvite = async (id) => {
    await revokeInvite(id);
    setInvites(prev => prev.filter(i => i.id !== id));
  };

  const settings = activeTeam?.settings || {};
  const TABS = [
    { id: 'feed',     label: 'Feed',     icon: '📋' },
    ...(isTeamAdmin && pendingCount > 0 ? [{ id: 'pending', label: `Pending (${pendingCount})`, icon: '⏳' }] : isTeamAdmin ? [{ id: 'pending', label: 'Pending', icon: '⏳' }] : []),
    { id: 'skills',   label: 'Skills',   icon: '⚡' },
    { id: 'folders',  label: 'Folders',  icon: '📁' },
    { id: 'members',  label: 'Members',  icon: '👥' },
    { id: 'activity', label: 'Activity', icon: '📊' },
  ];

  const actionActivity = (a) => {
    const map = {
      entry_added:    '📋 added a prompt',
      entry_approved: '✓ approved a prompt',
      invite_sent:    '✉ sent an invite',
      member_joined:  '👋 joined the team',
      skill_created:  '⚡ created a skill',
    };
    return map[a.action] || a.action;
  };

  if (loadingTeams) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, color: 'var(--dim)', fontFamily: 'var(--font)', fontSize: 13 }}>
      Loading team...
    </div>
  );
  if (!activeTeam) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, color: 'var(--dim)', fontFamily: 'var(--font)', fontSize: 13 }}>
      No team selected — pick one from the sidebar
    </div>
  );

  return (
    <div style={{ display: 'flex', flex: 1, height: '100%', maxHeight: '100%', overflow: 'hidden', background: 'var(--bg)', fontFamily: 'var(--font)' }}>
      <div className="team-scroll-area" style={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', height: '100%' }}>
      <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, flexShrink: 0,
            background: 'var(--accent-dim)', border: '0.5px solid var(--accent-border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
          }}>🏢</div>
          <div>
            <div style={s.pageTitle}>{activeTeam.name}</div>
            <div style={{ fontSize: 12, color: 'var(--dim)', display: 'flex', gap: 8 }}>
              <span style={{ color: 'var(--accent)', textTransform: 'capitalize' }}>{activeTeam.plan}</span>
              <span>·</span>
              <span>{members.length || '…'} members</span>
              <span>·</span>
              <RoleBadge role={teamRole} />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isTeamAdmin && (
            <button onClick={() => setShowInvite(true)} style={s.newBtn}>+ Invite</button>
          )}
          {isTeamAdmin && (
            <Link to="/team/settings" style={{ ...s.backBtn, textDecoration: 'none' }}>⚙ Settings</Link>
          )}

        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Total prompts"   value={entries.length}      sub={`${entries.length} approved`} />
        <StatCard label="Pending review"  value={pendingCount}         color={pendingCount > 0 ? '#ef9f27' : undefined} />
        <StatCard label="Team skills"     value={skills.length}        color="var(--accent)" />
        <StatCard label="Members"         value={members.length || '…'} />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 18 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '7px 14px', border: '0.5px solid',
            borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font)', fontSize: 13, cursor: 'pointer',
            background: tab === t.id ? 'var(--accent-dim)' : 'none',
            borderColor: tab === t.id ? 'var(--accent-border)' : 'var(--border2)',
            color: tab === t.id ? 'var(--accent)' : 'var(--muted)',
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <span style={{ fontSize: 12 }}>{t.icon}</span> {t.label}
            {t.id === 'feed' && pendingCount > 0 && (
              <span style={{ background: '#ef9f27', color: '#fff', fontSize: 9, padding: '1px 5px', borderRadius: 8, marginLeft: 2 }}>
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--dim)', fontSize: 13 }}>Loading...</div>
      ) : (
        <>
          {/* ── Feed ── */}
          {tab === 'feed' && (
            <>
              <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
                  <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--dim)', fontSize: 14, pointerEvents: 'none' }}>⌕</span>
                  <input style={{ ...s.search, paddingLeft: 30 }} placeholder="Search team prompts..."
                    value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <select style={{ ...s.search, width: 'auto', flex: 'none' }}
                  value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                  <option value="all">All types</option>
                  {OUTPUT_TYPES.map(t => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
                </select>

              </div>

              {/* Folder filter bar */}
              {teamFolders.length > 0 && (
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 12 }}>
                  <button
                    onClick={() => setActiveFolderFilter(null)}
                    style={{
                      padding: '5px 13px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                      fontFamily: 'var(--font)', fontWeight: activeFolderFilter === null ? 600 : 400,
                      background: activeFolderFilter === null ? 'var(--accent-dim)' : 'var(--surface2)',
                      border: `0.5px solid ${activeFolderFilter === null ? 'var(--accent-border)' : 'var(--border2)'}`,
                      color: activeFolderFilter === null ? 'var(--accent)' : 'var(--muted)',
                      transition: 'all 0.12s',
                    }}>
                    All
                  </button>
                  {teamFolders.map(f => (
                    <button
                      key={f.id}
                      onClick={() => setActiveFolderFilter(activeFolderFilter === f.id ? null : f.id)}
                      onDragOver={e => { e.preventDefault(); setDragOverFolderId(f.id); }}
                      onDragLeave={() => setDragOverFolderId(null)}
                      onDrop={e => { e.preventDefault(); if (draggingShareId) handleDropToFolder(draggingShareId, f.id); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '5px 13px', borderRadius: 20, fontSize: 12,
                        cursor: 'pointer', fontFamily: 'var(--font)',
                        fontWeight: activeFolderFilter === f.id ? 600 : 400,
                        background: dragOverFolderId === f.id
                          ? `${f.color || '#7f77dd'}25`
                          : activeFolderFilter === f.id ? `${f.color || '#7f77dd'}20` : 'var(--surface2)',
                        border: `0.5px solid ${dragOverFolderId === f.id || activeFolderFilter === f.id
                          ? (f.color || '#7f77dd') + '80'
                          : 'var(--border2)'}`,
                        color: activeFolderFilter === f.id ? (f.color || '#7f77dd') : 'var(--muted)',
                        transition: 'all 0.12s',
                        transform: dragOverFolderId === f.id ? 'scale(1.05)' : 'scale(1)',
                      }}>
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                        background: f.color || '#7f77dd',
                      }} />
                      {f.name}
                      {dragOverFolderId === f.id && (
                        <span style={{ fontSize: 10, color: f.color || '#7f77dd' }}>Drop</span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {filteredEntries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--dim)' }}>
                  <div style={{ fontSize: 36, marginBottom: 10, opacity: 0.4 }}>📋</div>
                  <div style={{ fontSize: 14, color: 'var(--muted)' }}>No team prompts yet</div>
                  <div style={{ fontSize: 13, marginTop: 6 }}>Share entries to this team from your personal library using the Share to team button on any prompt.</div>
                </div>
              ) : (
                <div style={s.table}>
                  <div style={s.thead}>
                    <div style={{ flex: 3 }}>Prompt</div>
                    <div style={{ flex: 1 }}>Type</div>
                    <div style={{ flex: 1.5 }}>Model</div>
                    <div style={{ flex: 1.5 }}>Added by</div>
                    <div style={{ flex: 1 }}>Status</div>
                    <div style={{ flex: 1 }}>Date</div>
                    {isTeamAdmin && settings.approval_required && <div style={{ flex: 1.5 }}>Actions</div>}
                  </div>
                  {filteredEntries.map(entry => (
                    <div
                      key={entry.id}
                      onClick={() => setSelectedEntry(entry)}
                      draggable={!!entry.shareId}
                      onDragStart={() => setDraggingShareId(entry.shareId)}
                      onDragEnd={() => { setDraggingShareId(null); setDragOverFolderId(null); }}
                      style={{
                        ...s.trow,
                        cursor: 'grab',
                        opacity: draggingShareId === entry.shareId ? 0.5 : 1,
                        transition: 'opacity 0.1s',
                      }}>
                      <div style={{ flex: 3, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                        {/* Thumbnail — image/video get a real preview, others get icon */}
                        <div style={{
                          width: 44, height: 36, borderRadius: 6, flexShrink: 0,
                          background: 'var(--surface2)', border: '0.5px solid var(--border)',
                          overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 18,
                        }}>
                          {entry.file_url && entry.type === 'image' ? (
                            <img src={entry.file_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : entry.file_url && entry.type === 'video' ? (
                            <video src={entry.file_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
                          ) : (
                            <span>{TYPE_ICONS[entry.type]}</span>
                          )}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                            {entry.title}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                            {entry.prompt?.slice(0, 60)}...
                          </div>
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <span className={`tag tag-${entry.type}`} style={{ fontSize: 10 }}>{entry.type}</span>
                      </div>
                      <div style={{ flex: 1.5, fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {entry.model}
                      </div>
                      <div style={{ flex: 1.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Avatar name={entry.profiles?.username} size={22} />
                        <span style={{ fontSize: 12, color: 'var(--muted)' }}>@{entry.profiles?.username || '—'}</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <span style={{
                          fontSize: 10, padding: '2px 7px', borderRadius: 4,
                          background: entry.shareStatus === 'pending' ? 'rgba(239,159,39,0.15)'
                            : entry.shareStatus === 'rejected' ? 'rgba(226,75,74,0.12)'
                            : 'rgba(29,158,117,0.12)',
                          color: entry.shareStatus === 'pending' ? '#ef9f27'
                            : entry.team_status === 'rejected' ? 'var(--danger)'
                            : '#5dcaa5',
                        }}>
                          {entry.team_status === 'pending' ? 'pending'
                            : entry.team_status === 'rejected' ? 'rejected'
                            : 'live'}
                        </span>
                      </div>
                      <div style={{ flex: 1, fontSize: 11, color: 'var(--dim)' }}>{formatDate(entry.created_at)}</div>
                      {isTeamAdmin && settings.approval_required && (
                        <div style={{ flex: 1.5, display: 'flex', gap: 5 }}>
                          {entry.team_status === 'pending' && (
                            <>
                              <button onClick={() => handleApprove(entry.id)}
                                style={{ ...s.actionBtn, color: '#5dcaa5', borderColor: 'rgba(29,158,117,0.3)' }}>✓</button>
                              <button onClick={() => handleReject(entry.id)}
                                style={{ ...s.actionBtn, color: 'var(--danger)', borderColor: 'rgba(226,75,74,0.3)' }}>✗</button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── Pending approval ── */}
          {tab === 'pending' && isTeamAdmin && (
            <>
              {pendingShares.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--dim)', fontSize: 13 }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>✓</div>
                  No pending entries — all caught up!
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {pendingShares.map(share => (
                    <div key={share.shareId} style={{
                      background: 'var(--surface)', border: '0.5px solid var(--border)',
                      borderRadius: 10, overflow: 'hidden',
                    }}>
                      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 3 }}>
                            {share.title || 'Untitled'}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--dim)', marginBottom: 6 }}>
                            Submitted by @{share.sharedBy?.username} · {new Date(share.sharedAt).toLocaleDateString()}
                          </div>
                          {share.prompt && (
                            <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, background: 'var(--surface2)', borderRadius: 6, padding: '8px 10px', fontFamily: 'var(--mono)' }}>
                              {share.prompt.slice(0, 200)}{share.prompt.length > 200 ? '…' : ''}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ padding: '10px 16px', borderTop: '0.5px solid var(--border)', display: 'flex', gap: 8, background: 'var(--bg)' }}>
                        <button
                          onClick={() => handleApprove(share.shareId)}
                          style={{
                            background: 'rgba(29,158,117,0.15)', color: '#1d9e75',
                            border: '0.5px solid rgba(29,158,117,0.4)',
                            borderRadius: 7, padding: '7px 16px',
                            fontFamily: 'var(--font)', fontSize: 12.5, fontWeight: 500, cursor: 'pointer',
                          }}>
                          ✓ Approve
                        </button>
                        <button
                          onClick={() => {
                            const note = window.prompt('Reason for rejection (optional):') ?? '';
                            handleReject(share.shareId, note);
                          }}
                          style={{
                            background: 'rgba(226,75,74,0.1)', color: 'var(--danger)',
                            border: '0.5px solid rgba(226,75,74,0.3)',
                            borderRadius: 7, padding: '7px 14px',
                            fontFamily: 'var(--font)', fontSize: 12.5, cursor: 'pointer',
                          }}>
                          ✗ Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── Folders ── */}
          {tab === 'folders' && (
            <TeamFoldersTab
              teamId={teamId}
              folders={teamFolders}
              isTeamAdmin={isTeamAdmin}
              settings={activeTeam?.settings || {}}
              onCreateFolder={handleCreateFolder}
              onDeleteFolder={handleDeleteFolder}
              onUpdateFolder={handleUpdateFolder}
            />
          )}

          {/* ── Members ── */}
          {tab === 'members' && (
            <>
              <div style={s.table}>
                <div style={s.thead}>
                  <div style={{ flex: 2.5 }}>Member</div>
                  <div style={{ flex: 1 }}>Role</div>
                  <div style={{ flex: 1 }}>Prompts</div>
                  <div style={{ flex: 1 }}>Skills</div>
                  <div style={{ flex: 1.5 }}>Last active</div>
                  {isTeamAdmin && <div style={{ flex: 2 }}>Actions</div>}
                </div>
                {members.map(m => {
                  const stats = memberStats.find(s => s.user_id === m.user_id) || {};
                  const isSelf = m.user_id === user?.id;
                  return (
                    <div key={m.user_id} style={s.trow}>
                      <div style={{ flex: 2.5, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar name={m.username} size={30} />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>
                            {m.display_name || m.username}
                            {isSelf && <span style={{ fontSize: 10, color: 'var(--dim)', marginLeft: 6 }}>you</span>}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--dim)' }}>@{m.username}</div>
                        </div>
                      </div>
                      <div style={{ flex: 1 }}><RoleBadge role={m.role} /></div>
                      <div style={{ flex: 1, fontSize: 13, color: 'var(--muted)' }}>{stats.entry_count ?? 0}</div>
                      <div style={{ flex: 1, fontSize: 13, color: 'var(--muted)' }}>{stats.skill_count ?? 0}</div>
                      <div style={{ flex: 1.5, fontSize: 11, color: 'var(--dim)' }}>
                        {stats.last_entry_at ? formatDate(stats.last_entry_at) : '—'}
                      </div>
                      {isTeamAdmin && !isSelf && m.role !== 'owner' && (
                        <div style={{ flex: 2, display: 'flex', gap: 5 }}>
                          {m.role === 'member' && (
                            <button onClick={() => handleRoleChange(m.user_id, 'admin')} style={s.actionBtn}>
                              Make admin
                            </button>
                          )}
                          {m.role === 'admin' && (
                            <button onClick={() => handleRoleChange(m.user_id, 'member')} style={s.actionBtn}>
                              To member
                            </button>
                          )}
                          <button onClick={() => handleRemoveMember(m.user_id)}
                            style={{ ...s.actionBtn, color: 'var(--danger)', borderColor: 'rgba(226,75,74,0.3)' }}>
                            Remove
                          </button>
                        </div>
                      )}
                      {isTeamAdmin && (isSelf || m.role === 'owner') && <div style={{ flex: 2 }} />}
                    </div>
                  );
                })}
              </div>

              {/* Pending invites */}
              {isTeamAdmin && invites.filter(i => i.status === 'pending').length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <div style={s.sectionTitle}>Pending invites</div>
                  <div style={s.table}>
                    <div style={s.thead}>
                      <div style={{ flex: 3 }}>Email</div>
                      <div style={{ flex: 1 }}>Role</div>
                      <div style={{ flex: 1.5 }}>Sent</div>
                      <div style={{ flex: 1.5 }}>Expires</div>
                      <div style={{ flex: 1 }}>Actions</div>
                    </div>
                    {invites.filter(i => i.status === 'pending').map(inv => (
                      <div key={inv.id} style={s.trow}>
                        <div style={{ flex: 3, fontSize: 13, color: 'var(--text)' }}>{inv.email}</div>
                        <div style={{ flex: 1 }}><RoleBadge role={inv.role} /></div>
                        <div style={{ flex: 1.5, fontSize: 11, color: 'var(--dim)' }}>{formatDate(inv.created_at)}</div>
                        <div style={{ flex: 1.5, fontSize: 11, color: new Date(inv.expires_at) < new Date() ? 'var(--danger)' : 'var(--dim)' }}>
                          {formatDate(inv.expires_at)}
                        </div>
                        <div style={{ flex: 1 }}>
                          <button onClick={() => handleRevokeInvite(inv.id)}
                            style={{ ...s.actionBtn, color: 'var(--danger)', borderColor: 'rgba(226,75,74,0.3)' }}>
                            Revoke
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Skills ── */}
          {tab === 'skills' && (
            <div>
              {skills.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--dim)' }}>
                  <div style={{ fontSize: 36, marginBottom: 10, opacity: 0.4 }}>⚡</div>
                  <div style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 6 }}>No team skills yet</div>
                  {isTeamAdmin && (
                    <Link to="/skills" style={{ ...s.newBtn, display: 'inline-block', textDecoration: 'none', marginTop: 12 }}>
                      Create team skill
                    </Link>
                  )}
                </div>
              ) : (
                <div style={s.table}>
                  <div style={s.thead}>
                    <div style={{ flex: 3 }}>Skill</div>
                    <div style={{ flex: 1 }}>Type</div>
                    <div style={{ flex: 1.5 }}>Created by</div>
                    <div style={{ flex: 1 }}>Uses</div>
                    <div style={{ flex: 1 }}>Locked</div>
                  </div>
                  {skills.map(sk => (
                    <div key={sk.id} style={s.trow}>
                      <div style={{ flex: 3 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{sk.title}</div>
                        {sk.description && <div style={{ fontSize: 11, color: 'var(--dim)' }}>{sk.description}</div>}
                      </div>
                      <div style={{ flex: 1 }}><span className={`tag tag-${sk.type}`} style={{ fontSize: 10 }}>{sk.type}</span></div>
                      <div style={{ flex: 1.5, fontSize: 12, color: 'var(--muted)' }}>@{sk.profiles?.username || '—'}</div>
                      <div style={{ flex: 1, fontSize: 12, color: 'var(--muted)' }}>{sk.use_count}</div>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: 11, color: sk.team_locked ? 'var(--accent)' : 'var(--dim)' }}>
                          {sk.team_locked ? '🔒 Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Activity ── */}
          {tab === 'activity' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {activity.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--dim)', fontSize: 13 }}>No activity yet</div>
              ) : activity.map(a => (
                <div key={a.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                  borderBottom: '0.5px solid var(--border)', borderRadius: 'var(--radius-sm)',
                }}>
                  <Avatar name={a.profiles?.username} size={28} />
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>
                      @{a.profiles?.username}
                    </span>
                    <span style={{ fontSize: 13, color: 'var(--muted)', marginLeft: 6 }}>
                      {actionActivity(a)}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--dim)', flexShrink: 0 }}>{formatDate(a.created_at)}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      </div>
      </div>

      {/* Entry detail panel — slides in from right on desktop */}
      {selectedEntry && (
        <DetailPanel
          entry={selectedEntry}
          onClose={() => setSelectedEntry(null)}
          onDeleted={(id) => {
            setSelectedEntry(null);
            setEntries(prev => prev.filter(e => e.id !== id));
          }}
          onUpdated={(updated) => {
            setEntries(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
            setSelectedEntry(prev => ({ ...prev, ...updated }));
          }}
          folders={[]}
        />
      )}

      {showInvite && (
        <InviteModal
          team={activeTeam}
          onClose={() => setShowInvite(false)}
          onInvited={() => { setShowInvite(false); if (tab === 'members') { setTab('members'); } }}
        />
      )}
    </div>
  );
}

const s = {
  page:      { background: 'var(--bg)', padding: '24px', fontFamily: 'var(--font)' },
  header:    { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 12, flexWrap: 'wrap' },
  pageTitle: { fontSize: 20, fontWeight: 500, color: 'var(--text)', letterSpacing: '-0.02em', marginBottom: 2 },
  backBtn:   { background: 'none', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-sm)', padding: '7px 14px', color: 'var(--muted)', fontFamily: 'var(--font)', fontSize: 13, cursor: 'pointer' },
  newBtn:    { background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', padding: '8px 16px', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  sectionTitle: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--dim)', marginBottom: 10, fontWeight: 500 },
  search:    { width: '100%', background: 'var(--surface)', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-sm)', padding: '7px 12px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, outline: 'none' },
  table:     { background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'auto' },
  thead:     { display: 'flex', gap: 12, padding: '10px 16px', background: 'var(--surface2)', borderBottom: '0.5px solid var(--border)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--dim)', minWidth: 500 },
  trow:      { display: 'flex', gap: 12, padding: '12px 16px', borderBottom: '0.5px solid var(--border)', alignItems: 'center', minWidth: 500 },
  actionBtn: { background: 'none', border: '0.5px solid var(--border2)', borderRadius: 4, padding: '4px 10px', fontFamily: 'var(--font)', fontSize: 11, color: 'var(--muted)', cursor: 'pointer', whiteSpace: 'nowrap' },
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 },
  modal:     { background: 'var(--surface)', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: 480 },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '0.5px solid var(--border)' },
  closeBtn:  { background: 'none', border: 'none', color: 'var(--dim)', fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: 2 },
  field:     { display: 'flex', flexDirection: 'column', gap: 5 },
  label:     { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--dim)', fontWeight: 500 },
  input:     { background: 'var(--bg)', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, outline: 'none', width: '100%' },
  cancelBtn: { background: 'none', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-sm)', padding: '9px 16px', fontFamily: 'var(--font)', fontSize: 13, color: 'var(--muted)', cursor: 'pointer' },
  saveBtn:   { flex: 1, background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', padding: '9px', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
};
